/**
 * RecipeListContainer
 * --
 */
import React from 'react';
import RecipeListPresenter from './RecipeListPresenter';
import { useGetMapGroupProducts } from 'hooks/GroupProductHooks';
import MessageAlert from 'utils';
import {
  useUpsertGroupProduct,
  groupProductQueryKeys,
  useDeleteGroupProduct,
} from 'hooks/GroupProductHooks';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Logger from 'utils/logger';
import Alert from 'utils/AlertManager';

const INITIAL_VALUE = {
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
};

const logger = new Logger('RecipeList');

const RecipeListContainer = () => {
  const queryClient = useQueryClient();

  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */

  /* ===== STATE ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);

  // 체크리스트
  const [selectedList, setSelectedList] = React.useState([]);
  const [sortItems, setSortItems] = React.useState([]);
  const [isOpened, setIsOpened] = React.useState(false);
  const [dateValue, setDateValue] = React.useState(INITIAL_VALUE);
  const [period, setPeriod] = React.useState({
    start_date: dayjs().subtract(1, 'months').startOf('day').unix(),
    end_date: dayjs().endOf('day').unix(),
  });

  /* ===== MUTATE ===== */
  const { upsertGroupProduct } = useUpsertGroupProduct();
  const { deleteGroupProduct } = useDeleteGroupProduct();

  /* ===== Data ===== */
  const { data: GroupProducts } = useGetMapGroupProducts(
    {
      start_date: period.start_date,
      end_date: period.end_date,
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 데이터 저장 함수
   * ---
   * @param {*} t
   * @param {*} v
   * @returns
   */
  const handleSubmit = async (t, v) => {
    const type = t.toLocaleUpperCase();
    const value = v.toLocaleUpperCase();
    const message = {
      IS_SHOW: {
        Y: '선택한 레시피를 목록에 노출 하시갰습니까?',
        N: '선택한 레시피를 목록에 노출하지 않으시갰습니까?',
      },
      IS_MAIN: {
        Y: '선택한 레시피를 메인에 노출 하시갰습니까?',
        N: '선택한 레시피를 메인에 노출하지 않으시갰습니까?',
      },
      SORT: {
        Y: '정렬된 순서대로 수정하시겠습니까?',
      },
    };

    if (!window.confirm(message[type][value])) return;
    let success = false;
    let selectList = selectedList;
    try {
      setIsLoading(true);
      if (['IS_SHOW', 'IS_MAIN'].includes(type)) {
        selectList = selectList?.map((_item) => ({
          group_product_id: _item.group_product_id,
          [t]: v,
        }));
      } else {
        selectList = sortItems?.map((_item, index) => ({
          group_product_id: _item.group_product_id,
          order: index + 1,
        }));
      }
      const { status } = await upsertGroupProduct(selectList);

      if (status === 200) {
        MessageAlert.success(`성공적으로 레시피상품이 수정되었습니다.`);
        success = true;
      }
    } catch (err) {
      console.error(`[UPDATE GROUP PRODUCT] ERROR : `, err);
      MessageAlert.error(`래시피을 수정하는데 오류가 발생하였습니다.`);
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        if (success) {
          const queryKey = groupProductQueryKeys.getMapGroupProducts({
            start_date: period.start_date,
            end_date: period.end_date,
          });
          queryClient.invalidateQueries({ queryKey: queryKey.queryKey });
          setIsOpened(false);
        }
      }, 1000);
    }
  };

  /**
   * ANCHOR 레시피 상품 삭제
   * --
   */
  const handleDelete = async () => {
    // 선택한 상품 없을 경우 예외 처리
    if (!selectedList.length) {
      MessageAlert.info('삭제하실 상품을 선택해주세요!');
      return;
    }

    // 상품 여러개 선택하였을 경우 예외 처리
    if (selectedList.length > 1) {
      MessageAlert.info('레시피 상품 삭제는 1건씩 가능합니다.');
      return;
    }

    if (
      !Alert.confirm(
        `레시피 상품을 정말 삭제하시겠습니까?\n삭제 후 복구 할 수 없습니다.`
      )
    )
      return;

    try {
      setIsLoading(true);

      const deleteItem = selectedList.at(0);

      if (!deleteItem) {
        MessageAlert.warning('해당 레시피 상품을 찾을 수 없습니다.');
        logger.warn('Not Found', '레시피 상품을 찾을 수 없슴!');
        return;
      }

      const { group_product_id } = deleteItem;

      const { status, message } = await deleteGroupProduct(group_product_id);

      if (status !== 200) {
        logger.warn('Delete Fail', message);
        MessageAlert.warning('레시피 상품 삭제에 실패하였습니다.');
        return;
      }

      logger.info('레시피 상품 삭제 실행');
      MessageAlert.success('레시피 상품이 정상적으로 삭제되었습니다.');

      // 캐싱 데이터 최신화
      const queryKey = groupProductQueryKeys.getMapGroupProducts({
        start_date: period.start_date,
        end_date: period.end_date,
      });
      queryClient.invalidateQueries({ queryKey: queryKey.queryKey });

      setSelectedList([]);
    } catch (err) {
      logger.error('Error!!', err);
      MessageAlert.error('레시피 상품 삭제 중 에러가 발생하였습니다.');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const newGroupProducts = React.useMemo(() => {
    return GroupProducts;
  }, [GroupProducts]);

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  const PROPS = {
    data: newGroupProducts,
    isLoading,
    setIsLoading,
    selectedList,
    onSelect: setSelectedList,
    onSubmit: handleSubmit,
    onSort: setSortItems,
    sortItems,
    isOpened,
    onOpen: setIsOpened,
    dateValue,
    setDateValue,
    setPeriod,
    INITIAL_VALUE,
    onDelete: handleDelete,
  };

  return (
    <>
      <RecipeListPresenter {...PROPS} />
    </>
  );
};

export default RecipeListContainer;
